import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// Async thunks
export const fetchSettlements = createAsyncThunk(
  'settlements/fetchSettlements',
  async ({ page = 1, limit = 20, status } = {}, { rejectWithValue }) => {
    try {
      let url = `/merchants/settlements?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch settlements');
    }
  }
);

export const fetchSettlementDetails = createAsyncThunk(
  'settlements/fetchSettlementDetails',
  async (settlementId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/merchants/settlements/${settlementId}`);
      return response.data.settlement;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch settlement details');
    }
  }
);

export const fetchSettlementLimits = createAsyncThunk(
  'settlements/fetchSettlementLimits',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/merchants/settlements/limits`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch settlement limits');
    }
  }
);

export const fetchAvailableBalance = createAsyncThunk(
  'settlements/fetchAvailableBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/merchants/settlements/available`);
      return response.data.balance;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch available balance');
    }
  }
);

export const requestSettlement = createAsyncThunk(
  'settlements/requestSettlement',
  async ({ amount }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/merchants/settlements/request`, { amount });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to request settlement');
    }
  }
);

export const resubmitSettlement = createAsyncThunk(
  'settlements/resubmitSettlement',
  async (settlementId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/merchants/settlements/${settlementId}/resubmit`, {});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to resubmit settlement');
    }
  }
);

// Settlement Agreement Thunks
export const fetchSettlementAgreementStatus = createAsyncThunk(
  'settlements/fetchAgreementStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/kyc/settlement-agreement/status`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch agreement status');
    }
  }
);

export const fetchSettlementAgreementContent = createAsyncThunk(
  'settlements/fetchAgreementContent',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/kyc/settlement-agreement/content`);
      return response.data.agreement;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch agreement content');
    }
  }
);

export const acceptSettlementAgreement = createAsyncThunk(
  'settlements/acceptAgreement',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/kyc/settlement-agreement/accept`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to accept agreement');
    }
  }
);

const settlementsSlice = createSlice({
  name: 'settlements',
  initialState: {
    items: [],
    selectedSettlement: null,
    stats: null,
    limits: null,
    availableBalance: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    },
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    detailsStatus: 'idle',
    detailsError: null,
    limitsStatus: 'idle',
    limitsError: null,
    balanceStatus: 'idle',
    balanceError: null,
    requestStatus: 'idle',
    requestError: null,
    resubmitStatus: 'idle',
    resubmitError: null,
    // Settlement Agreement
    agreementStatus: null, // { accepted, needsAcceptance, version, currentVersion }
    agreementContent: null,
    agreementStatusLoading: 'idle',
    agreementContentLoading: 'idle',
    agreementAcceptLoading: 'idle',
    agreementError: null
  },
  reducers: {
    clearSettlementError: (state) => {
      state.error = null;
      state.requestError = null;
    },
    clearSelectedSettlement: (state) => {
      state.selectedSettlement = null;
      state.detailsStatus = 'idle';
      state.detailsError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch settlements
      .addCase(fetchSettlements.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSettlements.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.settlements || [];
        state.stats = action.payload.stats || null;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchSettlements.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Fetch settlement details
      .addCase(fetchSettlementDetails.pending, (state) => {
        state.detailsStatus = 'loading';
        state.detailsError = null;
      })
      .addCase(fetchSettlementDetails.fulfilled, (state, action) => {
        state.detailsStatus = 'succeeded';
        state.selectedSettlement = action.payload;
      })
      .addCase(fetchSettlementDetails.rejected, (state, action) => {
        state.detailsStatus = 'failed';
        state.detailsError = action.payload;
      })
      // Fetch settlement limits
      .addCase(fetchSettlementLimits.pending, (state) => {
        state.limitsStatus = 'loading';
        state.limitsError = null;
      })
      .addCase(fetchSettlementLimits.fulfilled, (state, action) => {
        state.limitsStatus = 'succeeded';
        state.limits = action.payload;
      })
      .addCase(fetchSettlementLimits.rejected, (state, action) => {
        state.limitsStatus = 'failed';
        state.limitsError = action.payload;
      })
      // Fetch available balance
      .addCase(fetchAvailableBalance.pending, (state) => {
        state.balanceStatus = 'loading';
        state.balanceError = null;
      })
      .addCase(fetchAvailableBalance.fulfilled, (state, action) => {
        state.balanceStatus = 'succeeded';
        state.availableBalance = action.payload;
      })
      .addCase(fetchAvailableBalance.rejected, (state, action) => {
        state.balanceStatus = 'failed';
        state.balanceError = action.payload;
      })
      // Request settlement
      .addCase(requestSettlement.pending, (state) => {
        state.requestStatus = 'loading';
        state.requestError = null;
      })
      .addCase(requestSettlement.fulfilled, (state, action) => {
        state.requestStatus = 'succeeded';
        // Refresh items after successful request
      })
      .addCase(requestSettlement.rejected, (state, action) => {
        state.requestStatus = 'failed';
        state.requestError = action.payload;
      })
      // Resubmit settlement
      .addCase(resubmitSettlement.pending, (state) => {
        state.resubmitStatus = 'loading';
        state.resubmitError = null;
      })
      .addCase(resubmitSettlement.fulfilled, (state, action) => {
        state.resubmitStatus = 'succeeded';
        // Update the settlement in the items array
        const index = state.items.findIndex(item => item.id === action.payload.settlement.id);
        if (index !== -1) {
          state.items[index] = action.payload.settlement;
        }
      })
      .addCase(resubmitSettlement.rejected, (state, action) => {
        state.resubmitStatus = 'failed';
        state.resubmitError = action.payload;
      })
      // Settlement Agreement Status
      .addCase(fetchSettlementAgreementStatus.pending, (state) => {
        state.agreementStatusLoading = 'loading';
        state.agreementError = null;
      })
      .addCase(fetchSettlementAgreementStatus.fulfilled, (state, action) => {
        state.agreementStatusLoading = 'succeeded';
        state.agreementStatus = action.payload;
      })
      .addCase(fetchSettlementAgreementStatus.rejected, (state, action) => {
        state.agreementStatusLoading = 'failed';
        state.agreementError = action.payload;
      })
      // Settlement Agreement Content
      .addCase(fetchSettlementAgreementContent.pending, (state) => {
        state.agreementContentLoading = 'loading';
      })
      .addCase(fetchSettlementAgreementContent.fulfilled, (state, action) => {
        state.agreementContentLoading = 'succeeded';
        state.agreementContent = action.payload;
      })
      .addCase(fetchSettlementAgreementContent.rejected, (state, action) => {
        state.agreementContentLoading = 'failed';
        state.agreementError = action.payload;
      })
      // Accept Settlement Agreement
      .addCase(acceptSettlementAgreement.pending, (state) => {
        state.agreementAcceptLoading = 'loading';
      })
      .addCase(acceptSettlementAgreement.fulfilled, (state, action) => {
        state.agreementAcceptLoading = 'succeeded';
        // Update agreement status to accepted
        if (state.agreementStatus) {
          state.agreementStatus.accepted = true;
          state.agreementStatus.needsAcceptance = false;
          state.agreementStatus.version = action.payload.version;
          state.agreementStatus.acceptedAt = action.payload.acceptedAt;
        }
      })
      .addCase(acceptSettlementAgreement.rejected, (state, action) => {
        state.agreementAcceptLoading = 'failed';
        state.agreementError = action.payload;
      });
  }
});

export const { clearSettlementError, clearSelectedSettlement } = settlementsSlice.actions;
export default settlementsSlice.reducer;
